import React, { useEffect, useState } from "react";
import { AlertTriangle, Pencil, Loader2, MapPin } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { STORAGE_DETAILS } from "../lib/constants";
import { toast } from "sonner";

/**
 * Quick inline editor for a shipment's "Stocking Location" (Storage_Detail).
 * Designed for the Yard Runner Queue cards so the runner can re-rack a box
 * in 2 taps without opening the detail page.
 *
 * Behaviour:
 *  - Hidden when the shipment is NOT at "Main yard".
 *  - In view mode, shows the location (or yellow "Stocking Location Needed" badge).
 *  - Tapping (or pressing Edit) reveals a <select>.
 *  - Picking a new value or blurring a custom "Other Storage" input
 *    auto-saves via PATCH /api/shipments/{id} with { Storage_Detail }.
 *  - "Other Storage" prompts an inline text input that saves Storage_Other on blur.
 *
 * Optimistic update: parent's `onSaved(updated)` is called with the new shipment.
 */
export default function StockingLocationInlineEdit({ shipment, onSaved, testid }) {
  const atYard = shipment.Current_Location === "Main yard";
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [storageDetail, setStorageDetail] = useState(shipment.Storage_Detail || "");
  const [storageOther, setStorageOther] = useState(shipment.Storage_Other || "");

  useEffect(() => {
    setStorageDetail(shipment.Storage_Detail || "");
    setStorageOther(shipment.Storage_Other || "");
  }, [shipment.Storage_Detail, shipment.Storage_Other]);

  if (!atYard) return null;

  const displayValue =
    shipment.Storage_Detail === "Other Storage" && shipment.Storage_Other
      ? shipment.Storage_Other
      : shipment.Storage_Detail;
  const missing = !shipment.Storage_Detail;

  const persist = async (patch) => {
    setBusy(true);
    try {
      const { data } = await api.patch(`/shipments/${shipment.id}`, patch);
      toast.success("Stocking location saved");
      if (onSaved) onSaved(data);
      setEditing(false);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  const onPickDetail = (val) => {
    setStorageDetail(val);
    if (!val) return;
    if (val === "Other Storage") {
      // Persist the bucket selection now; user will type the free-text on the next field
      persist({ Storage_Detail: val });
      return;
    }
    // Clear any leftover free-text and save in one PATCH
    persist({ Storage_Detail: val, Storage_Other: "" });
  };

  const onBlurOther = () => {
    const trimmed = (storageOther || "").trim();
    if (trimmed === (shipment.Storage_Other || "").trim()) {
      // No change — just close the editor
      setEditing(false);
      return;
    }
    persist({ Storage_Other: trimmed });
  };

  // VIEW MODE
  if (!editing) {
    return (
      <button
        type="button"
        onClick={() => setEditing(true)}
        data-testid={testid || `stocking-edit-${shipment.MR_Number}`}
        title="Tap to change stocking location"
        className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-sm border-2 text-[11px] font-bold uppercase tracking-wider transition-colors ${
          missing
            ? "bg-amber-100 text-amber-900 border-amber-400 hover:bg-amber-200"
            : "bg-white text-slate-800 border-slate-300 hover:border-slate-900"
        }`}
      >
        {missing ? (
          <>
            <AlertTriangle strokeWidth={2.5} className="h-3 w-3" />
            Stocking Location Needed
          </>
        ) : (
          <>
            <MapPin strokeWidth={2.5} className="h-3 w-3 text-slate-500" />
            <span className="normal-case">{displayValue}</span>
          </>
        )}
        <Pencil strokeWidth={2.5} className="h-3 w-3 opacity-60" />
      </button>
    );
  }

  // EDIT MODE
  return (
    <span
      className="inline-flex items-center gap-1.5 flex-wrap"
      onClick={(e) => e.stopPropagation()}
      data-testid={`stocking-edit-row-${shipment.MR_Number}`}
    >
      <select
        autoFocus
        value={storageDetail}
        onChange={(e) => onPickDetail(e.target.value)}
        onBlur={() => {
          // If the user hasn't picked anything new and there's no "Other" prompt to fill, close.
          if (storageDetail === (shipment.Storage_Detail || "") && storageDetail !== "Other Storage") {
            setEditing(false);
          }
        }}
        disabled={busy}
        data-testid={`stocking-select-${shipment.MR_Number}`}
        className="rg-tap min-h-[36px] px-2 py-1 border-2 border-slate-900 rounded-sm bg-white text-xs font-bold uppercase tracking-wide focus:outline-none"
      >
        <option value="">— Pick a stocking spot —</option>
        {STORAGE_DETAILS.map((s) => (
          <option key={s} value={s}>
            {s}
          </option>
        ))}
      </select>
      {storageDetail === "Other Storage" && (
        <input
          autoFocus
          type="text"
          value={storageOther}
          onChange={(e) => setStorageOther(e.target.value)}
          onBlur={onBlurOther}
          onKeyDown={(e) => {
            if (e.key === "Enter") e.currentTarget.blur();
            if (e.key === "Escape") {
              setStorageOther(shipment.Storage_Other || "");
              setEditing(false);
            }
          }}
          disabled={busy}
          placeholder="Describe…"
          data-testid={`stocking-other-${shipment.MR_Number}`}
          className="rg-tap min-h-[36px] w-40 px-2 py-1 border-2 border-slate-900 rounded-sm bg-white text-xs focus:outline-none"
        />
      )}
      {busy && <Loader2 className="h-3.5 w-3.5 animate-spin text-slate-500" />}
      <button
        type="button"
        onClick={() => setEditing(false)}
        disabled={busy}
        data-testid={`stocking-cancel-${shipment.MR_Number}`}
        className="text-[10px] uppercase tracking-wider text-slate-500 hover:text-slate-900 font-bold"
      >
        Cancel
      </button>
    </span>
  );
}
